import React, { useCallback, useEffect, useState } from "react"
import axios from "axios"
import { UFS } from "../utils/licenciamento"

const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"]

interface Linha { dia: string; mes: string; finais: string; fonte: string; confirmado: boolean }
const linhaVazia = (): Linha => ({ dia: "", mes: "", finais: "", fonte: "", confirmado: true })
const doServidor = (l: any): Linha => ({ dia: l.dia ? String(l.dia) : "", mes: String(l.mes), finais: (l.finais || []).join(", "), fonte: l.fonte || "", confirmado: !!l.confirmado })

// CALENDARIO DE LICENCIAMENTO (so a Infotech): para cada estado e ano, em que dia e mes vence o documento de cada final de placa.
// O cadastro do veiculo usa isto para SUGERIR o vencimento real (UF + final da placa). Finais de 1 digito (1, 2...) valem para o ultimo digito;
// de 2 digitos (01, 11...) valem para os dois ultimos (como no Para).
export default function CalendarioLicenciamento() {
  const [uf, setUf] = useState("PA")
  const [ano, setAno] = useState<number>(new Date().getFullYear())
  const [anos, setAnos] = useState<number[]>([])
  const [linhas, setLinhas] = useState<Linha[]>([])
  const [resumo, setResumo] = useState<any[]>([])
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ texto: string; ok: boolean } | null>(null)

  const carregarResumo = useCallback(() => { axios.get(`${API}/licenciamento/calendario/resumo`).then((r) => setResumo(r.data?.cadastrados || [])).catch(() => {}) }, [])
  const carregar = useCallback(async (u: string, a?: number) => {
    setCarregando(true); setMsg(null)
    try {
      const r = await axios.get(`${API}/licenciamento/calendario`, { params: { uf: u, ...(a ? { ano: a } : {}) } })
      setAnos(r.data.anos || []); setAno(r.data.ano); setLinhas((r.data.linhas || []).map(doServidor))
    } catch (e: any) { setMsg({ texto: e?.response?.data?.erro || "Não foi possível carregar o calendário.", ok: false }); setLinhas([]) }
    finally { setCarregando(false) }
  }, [])
  useEffect(() => { carregarResumo() }, [carregarResumo])
  useEffect(() => { carregar(uf) }, [uf, carregar])

  const mudar = (i: number, campo: keyof Linha, valor: any) => setLinhas((ls) => ls.map((l, k) => (k === i ? { ...l, [campo]: valor } : l)))

  const salvar = async () => {
    for (const l of linhas) {
      if (!l.mes) return setMsg({ texto: "Escolha o mês em todas as linhas.", ok: false })
      if (!l.finais.trim()) return setMsg({ texto: "Informe os finais de placa em todas as linhas (ex.: 01, 11, 21).", ok: false })
    }
    setSalvando(true); setMsg(null)
    try {
      const corpo = { linhas: linhas.map((l) => ({ mes: Number(l.mes), dia: l.dia === "" ? null : Number(l.dia), finais: l.finais, fonte: l.fonte || undefined, confirmado: l.confirmado })) }
      const r = await axios.put(`${API}/licenciamento/calendario/${uf}/${ano}`, corpo)
      carregarResumo(); await carregar(uf, ano)   // recarrega do servidor (limpa a mensagem), e so depois avisa que salvou
      setMsg({ texto: `Calendário de ${uf} ${ano} salvo (${r.data?.terminacoes ?? 0} finais).`, ok: true })
    } catch (e: any) { setMsg({ texto: e?.response?.data?.erro || "Não foi possível salvar.", ok: false }) }
    finally { setSalvando(false) }
  }

  const anosOpcoes = Array.from(new Set([...anos, ano, new Date().getFullYear(), new Date().getFullYear() + 1])).sort((a, b) => b - a)

  return (
    <div style={{ padding: 24, height: "100%", overflowY: "auto" }}>
      <h2 style={{ fontSize: 20, fontWeight: 700 }}>📅 Calendário de licenciamento</h2>
      <p style={{ fontSize: 13, color: "#64748b", margin: "4px 0 16px", maxWidth: 760 }}>
        Em que dia e mês vence o licenciamento de cada final de placa, por estado e ano. O cadastro do veículo usa esta tabela para sugerir o vencimento real.
        Finais de 1 dígito (ex.: 1, 2) valem para o último dígito da placa; de 2 dígitos (ex.: 01, 11) valem para os dois últimos, como no Pará.
        Confira sempre no Detran do estado antes de marcar como conferido.
      </p>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }} aria-label="Estados com calendário">
        {UFS.map((u) => { const r = resumo.find((x) => x.uf === u); const sel = u === uf
          return (<button key={u} type="button" aria-pressed={sel} onClick={() => setUf(u)} title={r ? `${r.terminacoes} finais · ${r.confirmado ? "conferido" : "não conferido"} · ${r.ano}` : "sem calendário"}
            style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${sel ? "#0ea5e9" : "#2d4166"}`, background: sel ? "#0c2a4a" : "#0d1b2e", color: r ? (r.confirmado ? "#4ade80" : "#fbbf24") : "#64748b", cursor: "pointer", fontSize: 12, fontWeight: sel ? 700 : 500 }}>
            {u}{r ? (r.confirmado ? " ✓" : " ⚠") : ""}</button>) })}
      </div>

      <div className="card" style={{ maxWidth: 980 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "end", marginBottom: 14, flexWrap: "wrap" }}>
          <div className="form-group" style={{ width: 120 }}><label>Estado</label>
            <select aria-label="Estado" value={uf} onChange={(e) => setUf(e.target.value)}>{UFS.map((u) => <option key={u} value={u}>{u}</option>)}</select></div>
          <div className="form-group" style={{ width: 120 }}><label>Ano</label>
            <select aria-label="Ano" value={ano} onChange={(e) => carregar(uf, Number(e.target.value))}>{anosOpcoes.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
        </div>

        {carregando ? <div style={{ color: "#64748b", padding: 12 }}>Carregando...</div> : (
          <>
            {!linhas.length && <div style={{ color: "#64748b", fontSize: 13, marginBottom: 10 }}>Nenhum vencimento cadastrado para {uf} em {ano}. Adicione as linhas abaixo.</div>}
            <div style={{ display: "grid", gridTemplateColumns: "70px 150px 1fr 190px 90px 40px", gap: 8, fontSize: 11, color: "#64748b", marginBottom: 4 }}>
              <span>Dia</span><span>Mês</span><span>Finais da placa (separados por vírgula)</span><span>Fonte</span><span>Conferido</span><span />
            </div>
            {linhas.map((l, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "70px 150px 1fr 190px 90px 40px", gap: 8, marginBottom: 6, alignItems: "center" }}>
                <input aria-label={`Dia da linha ${i + 1}`} type="number" min={1} max={31} value={l.dia} placeholder="último" onChange={(e) => mudar(i, "dia", e.target.value)} />
                <select aria-label={`Mês da linha ${i + 1}`} value={l.mes} onChange={(e) => mudar(i, "mes", e.target.value)}><option value="">— mês —</option>{MESES.map((m, k) => <option key={k + 1} value={String(k + 1)}>{k + 1} - {m}</option>)}</select>
                <input aria-label={`Finais da linha ${i + 1}`} value={l.finais} placeholder="01, 11, 21, 31" onChange={(e) => mudar(i, "finais", e.target.value)} />
                <input aria-label={`Fonte da linha ${i + 1}`} value={l.fonte} placeholder="Detran-XX 2026" onChange={(e) => mudar(i, "fonte", e.target.value)} />
                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, fontSize: 12 }}><input type="checkbox" aria-label={`Conferido da linha ${i + 1}`} checked={l.confirmado} onChange={(e) => mudar(i, "confirmado", e.target.checked)} style={{ width: "auto" }} />sim</label>
                <button type="button" className="btn btn-ghost" aria-label={`Remover linha ${i + 1}`} style={{ padding: "4px 8px", color: "#f87171" }} onClick={() => setLinhas((ls) => ls.filter((_, k) => k !== i))}>✕</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" className="btn btn-ghost" onClick={() => setLinhas((ls) => [...ls, linhaVazia()])}>+ Adicionar linha</button>
              <button type="button" className="btn btn-primary" disabled={salvando} onClick={salvar}>{salvando ? "Salvando..." : `Salvar calendário de ${uf} ${ano}`}</button>
              <span style={{ fontSize: 11, color: "#64748b" }}>Dia em branco = último dia do mês. Salvar substitui o calendário deste estado e ano.</span>
            </div>
          </>
        )}
        {msg && <div role={msg.ok ? "status" : "alert"} style={{ marginTop: 12, fontSize: 13, color: msg.ok ? "#4ade80" : "#f87171" }}>{msg.texto}</div>}
      </div>
    </div>
  )
}
